import { match, P } from "ts-pattern";

import { createFlowTracker } from "../../lib/analytics/flowEvents";
import type { OnboardingVersionTag } from "../../lib/analytics/types";
import type { FlowState, FlowTransition } from "../../lib/flow-engine";
import { SingularEvents, singularService } from "../../lib/singular";
import type { OnboardingConfig } from "./OnboardingFlowV3";

// Shared by both shells (partner/FFGA and general): the flow config is one
// module, so one exhaustive tracker covers every outcome — FA arms simply
// never fire for general users, whose transitions skip those screens.

// Every outcome is tracked. `.exhaustive()` forces an arm when the config
// gains an outcome, and the tracker's constraints force the EventTypes entry
// and reject undeclared properties. Back/plain-continue funnel into the
// shared OnboardingBackOutcome / OnboardingContinueOutcome.
export const trackTransitionEvent = (
  event: FlowTransition<OnboardingConfig>,
  onboardingVersion: OnboardingVersionTag,
) => {
  const track = createFlowTracker("Onboarding", { onboardingVersion });
  match(event)
    .with({ from: { tag: P.select() }, outcome: { tag: "back" } }, from =>
      track.back(from),
    )
    .with({ from: { tag: P.select() }, outcome: { tag: "continue" } }, from =>
      track.continue(from),
    )
    .with(
      { from: { tag: "Intent" }, outcome: { tag: "selected" } },
      ({ outcome }) => {
        track.outcome("Intent", "selected", { intent: outcome.data.intent });
        // Singular is ad attribution, not Amplitude — it keeps its own event.
        singularService.event(SingularEvents.onboardingQuestionAnswered, {
          questionId: "intent",
          selections: outcome.data.intent,
        });
      },
    )
    .with(
      { from: { tag: "HouseholdSize" }, outcome: { tag: "selected" } },
      ({ outcome }) =>
        track.outcome("HouseholdSize", "selected", {
          householdSize: outcome.data.householdSize,
        }),
    )
    .with(
      { from: { tag: "Income" }, outcome: { tag: "checked" } },
      ({ outcome }) =>
        track.outcome("Income", "checked", { outcome: outcome.data.outcome }),
    )
    .with(
      { from: { tag: "Insurance" }, outcome: { tag: "selected" } },
      ({ outcome }) =>
        track.outcome("Insurance", "selected", {
          carrier: outcome.data.carrier,
        }),
    )
    .with(
      {
        from: { tag: "Insurance" },
        outcome: { tag: P.select(P.not("selected")) },
      },
      outcomeTag => track.outcome("Insurance", outcomeTag),
    )
    .with(
      { from: { tag: "Notifications" }, outcome: { tag: "configured" } },
      ({ outcome }) =>
        track.outcome("Notifications", "configured", {
          enabled: outcome.data.enabled,
        }),
    )
    .with({ from: { tag: "Notifications" }, outcome: { tag: "skipped" } }, () =>
      track.outcome("Notifications", "skipped"),
    )
    .with(
      {
        from: { tag: "ValueProps" },
        outcome: { tag: P.select(P.not("back")) },
      },
      outcomeTag => track.outcome("ValueProps", outcomeTag),
    )
    .with({ from: { tag: "Profile" }, outcome: { tag: "submitted" } }, () =>
      track.outcome("Profile", "submitted"),
    )
    .with(
      { from: { tag: "Phone" }, outcome: { tag: P.select() } },
      outcomeTag => track.outcome("Phone", outcomeTag),
    )
    .exhaustive();
};

// Screens with extra properties get dedicated/grouped arms; the rest fall
// through to the catch-all, whose tag constraint still demands an EventTypes
// entry. Grouping is by tag: the `needs: {}` sentinel is structurally open,
// so data-shape patterns cannot narrow the union — the tag is the only
// discriminant.
export const trackScreenEnteredEvent = (
  state: FlowState<OnboardingConfig>,
  onboardingVersion: OnboardingVersionTag,
) => {
  const track = createFlowTracker("Onboarding", { onboardingVersion });
  match(state)
    .with({ tag: "Income" }, ({ data }) =>
      track.screen("Income", {
        intent: data.intent,
        householdSize: data.householdSize,
      }),
    )
    .with(
      {
        tag: P.union(
          "BillEducation",
          "SaveEducation",
          "FinancialAssistance",
          "HouseholdSize",
          "FaResultStrongCase",
          "FaResultCatastrophic",
          "Insurance",
          "Notifications",
        ),
      },
      ({ tag, data }) => track.screen(tag, { intent: data.intent }),
    )
    .with({ tag: P.select() }, tag => track.screen(tag))
    .exhaustive();
};
